import axios from 'axios';

export const UPLOAD_RETRY_DELAYS_MS = [700, 1500, 3000];
export const UPLOAD_REQUEST_TIMEOUT_MS = 90000;

function sleep(delay) {
  return new Promise((resolve) => setTimeout(resolve, delay));
}

export function isTransientUploadError(error) {
  if (!error) return true;

  const status = error?.response?.status;
  const code = String(error?.code || '').toUpperCase();

  if (!status) return true;
  if (code === 'ECONNABORTED' || code === 'ERR_NETWORK' || code === 'ETIMEDOUT') return true;
  if (status === 408 || status === 409 || status === 425 || status === 429) return true;
  if (status >= 500) return true;

  return false;
}

function backendDetail(error) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail.trim();
  if (Array.isArray(detail) && detail.length) {
    const first = detail[0];
    if (typeof first === 'string') return first;
    if (typeof first?.msg === 'string') return first.msg;
  }
  if (typeof error?.response?.data?.message === 'string') return error.response.data.message;
  return '';
}

export function getUploadErrorMessage(error, fallback = 'Could not upload image. Please try again.') {
  const detail = backendDetail(error);
  if (detail) return detail;

  const status = error?.response?.status;
  if (status === 401 || status === 403) return 'Please sign in again, then retry the upload.';
  if (status === 413) return 'That image is too large. Please choose a smaller image.';
  if (status === 415) return 'That file type is not supported. Use JPG, PNG, WEBP or HEIC.';
  if (isTransientUploadError(error)) {
    return 'Upload could not complete after retrying. Please check your connection and try again.';
  }

  return error?.message || fallback;
}

export async function postUploadFormDataWithRetry(url, formData, config = {}, options = {}) {
  const attempts = Math.max(1, Number(options.attempts || 4));
  let lastError = null;

  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    try {
      return await axios.post(url, formData, {
        ...config,
        timeout: config.timeout || UPLOAD_REQUEST_TIMEOUT_MS,
      });
    } catch (error) {
      lastError = error;
      const shouldRetry = attempt < attempts && isTransientUploadError(error);
      if (!shouldRetry) throw error;

      const delay = UPLOAD_RETRY_DELAYS_MS[Math.min(attempt - 1, UPLOAD_RETRY_DELAYS_MS.length - 1)];
      if (typeof options.onRetry === 'function') {
        options.onRetry({ attempt, delay, error });
      }
      await sleep(delay);
    }
  }

  throw lastError;
}
